import type { PublicListingDataToRCM, PublicListingState } from './types'
import type { Selector } from 'store'

import { createSelector } from '@reduxjs/toolkit'
import { generateAddress } from '@rentspree/helper'
import isEmpty from 'lodash/isEmpty'

import { MAIN_PHOTO_HEIGHT } from 'containers/public-listing/gallery/constants'
import env from 'env'
import { parsePhotoGcsProtocol, resizePhoto } from 'utils/image-proxy/path'
import { capitalizeAll } from 'utils/string'

import {
  NAME,
  AMENITY_IN_UNIT_CATEGORIES,
  AMENITY_SHARED_CATEGORIES,
  AMENITY_INCLUDED_WITH_RENT_CATEGORIES,
  AMENITY_PUBLIC_LISTING_FEATURES,
} from './constant'

export const selectPublicListingState: Selector<PublicListingState> = (state) => state[NAME]

export const selectPublicListing: Selector<PublicListing> = createSelector(
  selectPublicListingState,
  (publicListingState) => publicListingState.data,
)

export const selectPublicListingAddressDetails: Selector<AddressDetails> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { city = '', street = '' } = publicListingState.addressDetails || {}

    return publicListingState.addressDetails
      ? {
          ...publicListingState.addressDetails,
          city: capitalizeAll(city),
          street: capitalizeAll(street),
        }
      : {}
  },
)

export const selectPublicListingGeneratedAddress: Selector<string> = createSelector(
  selectPublicListingAddressDetails,
  (address) => generateAddress(address),
)

export const selectPublicListingDataToRCM: Selector<PublicListingDataToRCM> = createSelector(
  [selectPublicListingGeneratedAddress, selectPublicListing],
  (address, publicListing) => ({
    id: publicListing?.propertyId ?? '',
    address,
  }),
)

export const selectPublicListingLocation: Selector<{ lat: number; lng: number } | undefined> =
  createSelector(selectPublicListing, (publicListingState: PublicListing) => {
    const {
      information: { lat, lng },
    } = publicListingState
    const isUndefined = [lat, lng].every((data) => data === undefined || data === null)
    if (isUndefined) return undefined
    return { lat: lat as number, lng: lng as number }
  })

export const selectInUnitAmenities: Selector<string[]> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { amenities = [], customAmenities = [] } = publicListingState

    const inUnitAmenityResult = amenities.filter((amenity) =>
      AMENITY_IN_UNIT_CATEGORIES.includes(amenity.category),
    )

    const customInUnitAmenityResult = customAmenities.filter((amenity) =>
      AMENITY_IN_UNIT_CATEGORIES.includes(amenity.category),
    )

    return [
      ...inUnitAmenityResult.map((amenity) => amenity.name),
      ...customInUnitAmenityResult.map((amenity) => amenity.name),
    ]
  },
)

export const selectSharedAmenities: Selector<string[]> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { amenities = [], customAmenities = [] } = publicListingState

    const sharedAmenityResult = amenities.filter((amenity) =>
      AMENITY_SHARED_CATEGORIES.includes(amenity.category),
    )

    const customSharedAmenityResult = customAmenities.filter((amenity) =>
      AMENITY_SHARED_CATEGORIES.includes(amenity.category),
    )

    return [
      ...sharedAmenityResult.map((amenity) => amenity.name),
      ...customSharedAmenityResult.map((amenity) => amenity.name),
    ]
  },
)

export const selectPublicListingPhotos: Selector<Photo[]> = createSelector(
  [selectPublicListingGeneratedAddress, selectPublicListing],
  (address: string, publicListingState: PublicListing) => {
    const {
      information: { photos },
    } = publicListingState

    return (
      photos?.map((photo, index, array) => {
        const width = photo.width ?? Number(env.IMAGE_PROXY_RESIZE_WIDTH) ?? 800
        const height = photo.height ?? Number(env.IMAGE_PROXY_RESIZE_HEIGHT) ?? 600

        const thumbnailHeight = MAIN_PHOTO_HEIGHT
        const thumbnailWidth = Math.round((thumbnailHeight * width) / height)

        const gsSrc = parsePhotoGcsProtocol(photo.src)
        const gsThumbnail = parsePhotoGcsProtocol(photo.thumbnail)
        const imageProxyHost = env.IMAGE_PROXY_HOST ?? ''

        return {
          ...photo,
          src: resizePhoto(imageProxyHost, gsSrc, width, height),
          thumbnail: resizePhoto(imageProxyHost, gsThumbnail, thumbnailWidth, thumbnailHeight),
          alt: `${index + 1} of ${array.length} at ${address}`,
        }
      }) ?? []
    )
  },
)

export const selectPublicListingVideo: Selector<string | undefined> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { information } = publicListingState
    return information?.video
  },
)

export const selectParkingAmenities: Selector<string[]> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const {
      leasingInformation: { parkingDetails = {} },
    } = publicListingState

    if (isEmpty(parkingDetails)) return []

    const parkingAmenityResult: string[] = []
    const { parkingType, parkingSpaces, evChargerAvailable } = parkingDetails

    if (parkingType) parkingAmenityResult.push(parkingType)
    if (parkingSpaces) parkingAmenityResult.push('Assigned Parking')
    if (evChargerAvailable) parkingAmenityResult.push('EV Charger')

    return parkingAmenityResult
  },
)

export const selectIncludedWithRent: Selector<string[]> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { amenities = [], customAmenities = [] } = publicListingState

    const includedWithRentResult = amenities.filter((amenity) =>
      AMENITY_INCLUDED_WITH_RENT_CATEGORIES.includes(amenity.category),
    )

    const customIncludedWithRentResult = customAmenities.filter((amenity) =>
      AMENITY_INCLUDED_WITH_RENT_CATEGORIES.includes(amenity.category),
    )

    return [
      ...includedWithRentResult.map((amenity) => amenity.name),
      ...customIncludedWithRentResult.map((amenity) => amenity.name),
    ]
  },
)

export const selectPublicListingFeatures: Selector<PublicListingFeature[]> = createSelector(
  selectPublicListingState,
  (publicListingState) => {
    const { amenities = [], customAmenities = [] } = publicListingState.data
    const featuredAmenities: PublicListingFeature[] = amenities.filter((amenity) =>
      AMENITY_PUBLIC_LISTING_FEATURES.includes(amenity.category),
    )
    const customFeaturedAmenities: PublicListingFeature[] = customAmenities.filter((amenity) =>
      AMENITY_PUBLIC_LISTING_FEATURES.includes(amenity.category),
    )
    return featuredAmenities.concat(customFeaturedAmenities)
  },
)

export const selectPublicListingPropertyDetails: Selector<PublicListingPropertyDetails> =
  createSelector(selectPublicListing, (publicListingState: PublicListing) => {
    const { propertyDetails, leasingInformation } = publicListingState
    return {
      price: leasingInformation?.pricingDetails?.monthlyRent || 0,
      beds: propertyDetails?.bedrooms || 0,
      baths: propertyDetails?.bathrooms || 0,
      sqft: propertyDetails?.squareFeet || 0,
      propertyType: propertyDetails?.propertyType || 'Other',
      yearBuilt: propertyDetails?.yearBuilt || 0,
    }
  })

export const selectPublicListingLeasingInformation: Selector<LeasingInformation> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { leasingInformation } = publicListingState
    return leasingInformation
  },
)

export const selectPublicListingContactDetails: Selector<ContactDetails> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { contactDetails } = publicListingState
    return contactDetails || ({} as ContactDetails)
  },
)

export const selectPublicListingDescription: Selector<string> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { information } = publicListingState
    return information?.description || ''
  },
)

export const selectPublicListingScreeningRequest: Selector<ScreeningRequest> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => {
    const { screeningRequest = {}, disabledFeatures = [] } = publicListingState

    const isDisabled = disabledFeatures.includes('listingApplication')
    if (isDisabled) return {}

    return screeningRequest || {}
  },
)

export const selectSharingMetaProperties: Selector<SharingMetaProperties> = createSelector(
  [
    selectPublicListingGeneratedAddress,
    selectPublicListingPropertyDetails,
    selectPublicListingScreeningRequest,
    selectPublicListingPhotos,
  ],
  (address, propertyDetails, screeningRequest, photos) => {
    const { price, beds, baths, sqft, propertyType } = propertyDetails
    const { status } = screeningRequest
    const isAvailable = status === 'ENABLED'
    const description = `${address} is a ${propertyType} that contains ${sqft} sqft. It contains ${beds} bedrooms and ${baths} bathrooms. ${
      isAvailable
        ? `This rental unit is available for $${price} per month.`
        : 'This rental unit is not available.'
    }`

    return {
      title: `${address} | RentSpree`,
      description,
      url: env.DOMAIN_NAME,
      image: photos?.[0]?.thumbnail ?? '',
    }
  },
)

export const selectPublicListingPartnerName: Selector<string> = createSelector(
  selectPublicListing,
  (publicListingState: PublicListing) => publicListingState.partnerName,
)
